import { useState } from "react"
import cx from "classnames"
import {
  useCheckout,
  AddressElement,
  PaymentElement,
} from "@stripe/react-stripe-js"
import {
  StripeAddressElementChangeEvent,
  StripePaymentElementChangeEvent,
} from "@stripe/stripe-js"

import { heapTrackEvent } from "../../api/integrations"
import { genericErrMsg } from "../../api/auth"
import { SubmitButton } from "../../components/SubmitButton"
import { Toast } from "../../components/Toast"

export interface PaywallCheckoutWidgetProps {
  price: string
  isMonthly: boolean
  priceYearly: string
  joinBtnClass?: string
}

export interface CheckoutResult {
  type: "error" | "success"
}

const PaywallCheckoutWidget = ({
  price,
  isMonthly,
  priceYearly,
  joinBtnClass,
}: PaywallCheckoutWidgetProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  // make sure we only track the first time user inputs in payment/address form
  const [trackedPaymentFormInput, setTrackedPaymentFormInput] = useState(false)
  const [trackedAddressInput, setTrackedAddressInput] = useState(false)
  const checkout = useCheckout()

  const handleSubmitCheckout = async (): Promise<CheckoutResult> => {
    return await checkout.confirm()
  }

  const handleSubmit = () => {
    setIsSubmitting(true)
    handleSubmitCheckout()
      .then((result) => {
        if (result.type === "error") {
          // Handle error
          // success will redirect to where they came from
          Toast.error(genericErrMsg)
          setIsSubmitting(false)
        }
        setIsSubmitting(false)
      })
      .catch(() => {
        Toast.error(genericErrMsg)
        setIsSubmitting(false)
      })
  }

  const handlePaymentOnChange = (event: StripePaymentElementChangeEvent) => {
    // track once if payment form has any inputs from user
    if (!event.empty && !trackedPaymentFormInput) {
      setTrackedPaymentFormInput(true)
      heapTrackEvent("start-stripe-embedded-payment-form")
    }
  }

  const handleAddressOnChange = (event: StripeAddressElementChangeEvent) => {
    // track once if address form has any inputs from user
    if (!event.empty && !trackedAddressInput) {
      setTrackedAddressInput(true)
      heapTrackEvent("start-stripe-embedded-address-form")
    }
  }

  return (
    <>
      <div className="my-6">
        <h3
          className="text-3xl font-display font-medium leading-[124%] tracking-[0.6px]"
          data-testid="paywall-checkout-widget-price"
        >
          Price: {`${price}/month`}
        </h3>

        <div>
          {!isMonthly ? (
            <p
              className="block text-lg font-body leading-[140%] tracking-[0.36px] mt-2"
              data-testid="paywall-checkout-widget-price-yearly"
            >
              Billed {priceYearly}/yearly. Cancel anytime.
            </p>
          ) : (
            <p
              className="block text-lg font-body leading-[140%] tracking-[0.36px] mt-2"
              data-testid="paywall-checkout-widget-price-monthly"
            >
              Billed monthly. Cancel anytime.
            </p>
          )}

          <p
            className="inline-block text-base font-body leading-[140%] tracking-[0.32px]"
            data-testid="paywall-checkout-widget-disclaimer"
          >
            Your membership includes advisory for up to 3 parcels.
          </p>
        </div>

        <h3
          className="text-autumn font-bold font-body text-lg leading-[140%] tracking-[0.36px] mt-6 mb-4"
          data-testid="paywall-checkout-widget-checkout-heading"
        >
          Checkout
        </h3>

        <PaymentElement
          id="stripe-payment-element"
          options={{ layout: "tabs" }}
          onChange={handlePaymentOnChange}
        />

        <p
          className="text-autumn text-base font-bold leading-[140%] tracking-[0.4px] mt-16 mb-3"
          data-testid="paywall-checkout-widget-billing-address"
        >
          Billing Address
        </p>

        <AddressElement
          id="stripe-address-element"
          options={{
            mode: "billing",
          }}
          onChange={handleAddressOnChange}
        />
      </div>

      <div className="flex flex-col items-end mb-10">
        <SubmitButton
          className={cx(
            "btn2 btn2-primary font-semibold sm:btn2-inline-block sm:inline-block",
            joinBtnClass
          )}
          isSubmitting={isSubmitting}
          onClick={() => {
            handleSubmit()
          }}
          disabled={!checkout.canConfirm && !isSubmitting}
          data-testid="paywall-checkout-widget-submit-button"
        >
          Pay now
        </SubmitButton>
      </div>
    </>
  )
}

export default PaywallCheckoutWidget
